import type {
    Campaign,
    CampaignFilter,
} from "@campaign/domain/models/campaign.model";
import { Campaigns } from "@campaign/domain/models/campaign.model";
import { CropTypes } from "@campaign/domain/models/crop-type.model";
import { CampaignGetAllUseCase } from "@campaign/domain/usecases/campaign-get-all.usecase";
import { CropTypeGetAllUseCase } from "@campaign/domain/usecases/crop-type-get-all.usecase";
import { Pagination } from "@core/domain/models/pagination.model";
import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import { SpatialLevels } from "@station/domain/models/spatial-level.model";
import type { Station } from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase.ts";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";
@injectable()
export class CampaignComponentViewmodel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    stations: Readonly<Station>[] = [];

    @observable
    cropTypes: Readonly<CropTypes> = CropTypes.empty();

    @observable
    showCreateCampaignModal = false;

    @observable
    showEditCampaignModal = false;

    @observable
    campaigns: Campaigns = Campaigns.empty();

    @observable
    campaign: Undefinable<Readonly<Campaign>> = undefined;

    @observable
    filters: CampaignFilter = {};

    @observable
    pagination: Pagination = Pagination.Default();

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
        @inject(CropTypeGetAllUseCase)
        private readonly cropTypeGetAllUseCase: CropTypeGetAllUseCase,
        @inject(CampaignGetAllUseCase)
        private readonly campaignGetAllUseCase: CampaignGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([this.fetchSpatialLevels(), this.fetchCropTypes()]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    toggleShowCreateCampaignModal(): void {
        this.showCreateCampaignModal = !this.showCreateCampaignModal;
    }

    @action
    toggleShowEditCampaignModal(): void {
        this.showEditCampaignModal = !this.showEditCampaignModal;
    }

    @action
    private async fetchSpatialLevels(): Promise<void> {
        this.spatialLevels = await this.spatialLevelGetAllUseCase.execute();
        runInAction(() => {
            this.stations = Array.from(
                this.spatialLevels.stationsMap.values(),
            ).filter((station) => station.isCampaignSelectable);
        });
    }

    @action
    private async fetchCropTypes(): Promise<void> {
        const cropTypes = await this.cropTypeGetAllUseCase.execute();

        runInAction(() => {
            this.cropTypes = cropTypes;
        });
    }

    @action
    async fetchCampaigns(filters?: CampaignFilter): Promise<void> {
        if (filters) {
            this.filters = filters;
            this.pagination = Pagination.Default();
        }

        const campaigns = await this.campaignGetAllUseCase.execute(
            this.pagination,
            this.filters,
        );

        runInAction(() => {
            this.campaigns = campaigns;
        });
    }

    async reloadCampaigns(): Promise<void> {
        LoadLayoutStore.start();
        await this.fetchCampaigns(this.filters);
        LoadLayoutStore.finish();
    }

    @action
    setCampaign(campaign: Undefinable<Readonly<Campaign>>): void {
        runInAction(() => {
            this.campaign = campaign;
        });
    }
}
